import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../ui/Avatar";
import {
  PEOPLE_PAGE_SIZE,
  daysSinceContact,
  primaryPhone,
  usePeople,
  usePeopleByTag,
  usePeopleSearch,
  usePeopleTotal,
  usePeopleWithoutTags,
  useUntaggedTotal,
  UNTAGGED_PAGE_SIZE,
  useTagDefs,
  useEventSearch,
  useNamedPeople,
  useSubjectSearch,
  type Person,
  type SubjectHit,
} from "../data";
import { deletePeople, linkDuplicateAsFamily, mergePeople } from "../actions";
import { useAuth } from "../auth";
import { NOUNS, count } from "../hebrew";
import {
  IcCalendar,
  IcCheck,
  IcChevron,
  IcClock,
  IcMerge,
  IcPeople,
  IcPhone,
  IcSearch,
  IcSpark,
  IcTent,
  IcTrash,
  IcWallet,
  IcWhatsapp,
} from "../ui/icons";
import { whatsappLink } from "@chbapark/domain/normalization";

/**
 * People is a way in, not a directory.
 *
 * An alphabetical list of a whole community is not something anyone reads top to
 * bottom, so it is not the default view. Search is the primary control; beneath
 * it are the two entry points that are actually useful — who was spoken to
 * recently, and the groups people belong to (spec §4.4).
 *
 * ## Two things the imported data forced onto this screen
 *
 * **Rows say what kind of record they are.** Searching "ראם כהן" returns three
 * rows: the man, the same man under a name variant, and a record named for him
 * and his wife. All three are real entries in the source data. Without a badge
 * the list looks broken; with one it reads as what it is — a family card, and a
 * duplicate to resolve.
 *
 * **Selecting many at once.** 3,893 people arrived and a good number are
 * fragments nobody wants. Deleting them one at a time is not a feature, it is a
 * punishment, so selection mode can take the whole visible list — or an entire
 * group — in one tap.
 */
export function PeoplePage() {
  const { role } = useAuth();
  const [pageSize, setPageSize] = useState(PEOPLE_PAGE_SIZE);
  const { data, loading } = usePeople(pageSize);
  const total = usePeopleTotal();
  const tagDefs = useTagDefs("person");
  const serviceDefs = useTagDefs("business_service");
  const [term, setTerm] = useState("");
  const [group, setGroup] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [untagged, setUntagged] = useState(false);
  const [untaggedSize, setUntaggedSize] = useState(UNTAGGED_PAGE_SIZE);

  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const isAdmin = role === "owner" || role === "admin";

  const search = usePeopleSearch(term);
  // The other two halves of one search box: the subjects on the timeline and
  // the events themselves. See `useSubjectSearch` for why the timeline needs a
  // collection-group query, an index and a rule of its own.
  const subjects = useSubjectSearch(term);
  const events = useEventSearch(term);
  const groupMembers = usePeopleByTag(group);
  const noTags = usePeopleWithoutTags(untagged ? untaggedSize : 0);
  const untaggedTotal = useUntaggedTotal();

  const tagNameById = useMemo(
    () => new Map([...tagDefs.data, ...serviceDefs.data].map((t) => [t.id, t.name ?? ""])),
    [tagDefs.data, serviceDefs.data],
  );

  const searching = term.trim().length > 0;

  const recent = useMemo(
    () =>
      data
        .filter((p) => daysSinceContact(p) != null)
        .sort((a, b) => (daysSinceContact(a) ?? 9999) - (daysSinceContact(b) ?? 9999))
        .slice(0, 5),
    [data],
  );

  const groups = useMemo(
    () =>
      tagDefs.data
        .filter((t) => t.status !== "hidden")
        .sort(
          (a, b) =>
            (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
            (a.name ?? "").localeCompare(b.name ?? "", "he"),
        )
        .map((t) => [t.id, t.name ?? ""] as const),
    [tagDefs.data],
  );

  const inGroup = useMemo(
    () =>
      [...groupMembers.data].sort((a, b) =>
        (a.displayName ?? "").localeCompare(b.displayName ?? "", "he"),
      ),
    [groupMembers.data],
  );

  const alphabetical = useMemo(
    () => [...data].sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", "he")),
    [data],
  );

  /** Whatever list is on screen right now. This is what "select all" means —
   *  selecting people who are not visible would be selecting blind. */
  const untaggedList = useMemo(
    () =>
      [...noTags.data].sort((a, b) =>
        (a.displayName ?? "").localeCompare(b.displayName ?? "", "he"),
      ),
    [noTags.data],
  );

  const visible: Person[] = searching
    ? search.data
    : group
      ? inGroup
      : untagged
        ? untaggedList
        : showAll
          ? alphabetical
          : [];

  function leaveSelection() {
    setSelecting(false);
    setSelected(new Set());
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>אנשי קשר</h1>
            <p className="sub">
              {loading
                ? "טוען…"
                : total === null
                  ? count(data.length, NOUNS.people)
                  : `${count(total, NOUNS.people)} במערכת`}
            </p>
          </div>
        </div>

        <div className="searchbar">
          <IcSearch size={18} />
          <input
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setGroup(null);
              setShowAll(false);
            }}
            placeholder="חיפוש לפי שם, טלפון או נושא…"
            autoComplete="off"
          />
        </div>
      </header>

      <div className="body">
        {loading ? (
          <p className="muted" style={{ textAlign: "center", padding: 22 }}>
            טוען…
          </p>
        ) : searching ? (
          results(search.data, term, search.error)
        ) : group ? (
          <>
            <button className="back-link" onClick={() => { setGroup(null); leaveSelection(); }}>
              <IcChevron size={15} /> חזרה
            </button>
            <PersonList
              people={inGroup}
              caption={`${tagNameById.get(group) ?? ""} · ${count(inGroup.length, NOUNS.people)}`}
              tagNameById={tagNameById}
              selecting={selecting}
              selected={selected}
              onToggle={toggle}
            />
          </>
        ) : untagged ? (
          /*
           * Everybody with no classification at all — 2,190 of 3,933.
           *
           * The product owner asked for "a tag for whoever has no tag", to make
           * clearing out the unrelated ones easy. A real tag would be the wrong
           * shape: it would have to be added and removed on every change and
           * would be stale within a day. This is the same question asked of a
           * counter the trigger keeps true.
           *
           * Loaded 500 at a time rather than 100. This list exists to be
           * emptied, and emptying it a hundred at a time is why it never got
           * emptied.
           */
          <>
            <button className="back-link" onClick={() => { setUntagged(false); leaveSelection(); }}>
              <IcChevron size={15} /> חזרה
            </button>
            <PersonList
              people={untaggedList}
              caption={
                untaggedTotal !== null && untaggedTotal > untaggedList.length
                  ? `ללא תגית · מוצגים ${untaggedList.length} מתוך ${untaggedTotal.toLocaleString("he-IL")}`
                  : `ללא תגית · ${count(untaggedList.length, NOUNS.people)}`
              }
              tagNameById={tagNameById}
              selecting={selecting}
              selected={selected}
              onToggle={toggle}
            />
            {untaggedTotal !== null && untaggedTotal > untaggedList.length && (
              <button
                className="btn btn-wide"
                style={{ margin: "4px 0 18px" }}
                onClick={() => setUntaggedSize((n) => n + UNTAGGED_PAGE_SIZE)}
              >
                הצגת עוד {UNTAGGED_PAGE_SIZE}
              </button>
            )}
          </>
        ) : showAll ? (
          <>
            <button className="back-link" onClick={() => { setShowAll(false); leaveSelection(); }}>
              <IcChevron size={15} /> חזרה
            </button>
            <PersonList
              people={alphabetical}
              caption={
                total !== null && total > data.length
                  ? `מוצגים ${data.length} מתוך ${total.toLocaleString("he-IL")}`
                  : `כל אנשי הקשר · ${data.length}`
              }
              tagNameById={tagNameById}
              selecting={selecting}
              selected={selected}
              onToggle={toggle}
            />
            {total !== null && total > data.length && (
              <button
                className="btn btn-wide"
                style={{ margin: "4px 0 18px" }}
                onClick={() => setPageSize((n) => n + PEOPLE_PAGE_SIZE)}
              >
                הצגת עוד {PEOPLE_PAGE_SIZE}
              </button>
            )}
          </>
        ) : data.length === 0 ? (
          <div className="empty">
            <div className="empty-ico">
              <IcPeople size={26} />
            </div>
            <h3>עדיין אין אנשי קשר</h3>
            <p>אנשי קשר נוספים לבד מטפסים שמגיעים מהאתר, או מייבוא היסטורי.</p>
          </div>
        ) : (
          <>
            {recent.length > 0 && (
              <section className="section">
                <div className="section-head">
                  <div
                    className="section-ico tint-slate"
                  >
                    <IcClock size={17} />
                  </div>
                  <div className="section-title">דיברתם איתם לאחרונה</div>
                </div>
                <PersonList people={recent} showDays tagNameById={tagNameById} />
              </section>
            )}

            {/*
              Who gives the most, in order.

              The figures were on every person and no screen ordered by them, so
              the answer to "who are our largest donors" was that you could not
              ask. Filtering by tag is part of the same control rather than a
              separate screen: "the biggest donors" is rarely the useful question
              and "the biggest donors in this group" usually is.
            */}
            {/*
              The way to find everybody nobody has classified.

              2,190 of 3,933 people carry no tag — the residue of an address-book
              import. Reaching them meant paging the whole list a hundred at a
              time, which is why they were still there.
            */}
            {(untaggedTotal ?? 0) > 0 && (
              <button
                className="card list-row"
                style={{ marginBottom: 12 }}
                onClick={() => {
                  setUntagged(true);
                  setUntaggedSize(UNTAGGED_PAGE_SIZE);
                }}
              >
                <span
                  className="rail-ico tint-slate"
                >
                  <IcTrash size={17} />
                </span>
                <span className="grow">
                  <span className="name-row">
                    ללא תגית
                  </span>
                  <span className="muted">
                    {(untaggedTotal ?? 0).toLocaleString("he-IL")} אנשי קשר שאף אחד לא סיווג. אפשר לסמן ולמחוק.
                  </span>
                </span>
                <IcChevron size={17} />
              </button>
            )}

            <Link to="/donors" className="card list-row" style={{ marginBottom: 12 }}>
              <span
                className="rail-ico tint-amber"
              >
                <IcWallet size={17} />
              </span>
              <span className="grow">
                <span className="name-row">
                  תורמים לפי סדר
                </span>
                <span className="muted">מהגדול לקטן, עם סינון לפי קבוצה</span>
              </span>
              <IcChevron size={17} />
            </Link>

            {groups.length > 0 && (
              <section className="section">
                <div className="section-head">
                  <div
                    className="section-ico tint-orange"
                  >
                    <IcPeople size={17} />
                  </div>
                  <div className="section-title">קבוצות ותחומי עניין</div>
                </div>
                <div className="card">
                  {groups.map(([tagId, name], i) => (
                    <button
                      key={tagId}
                      className="list-row"
                      onClick={() => setGroup(tagId)}
                      style={{ borderBottom: i < groups.length - 1 ? undefined : "0" }}
                    >
                      <span className="grow">{name}</span>
                      <span style={{ color: "var(--ink-3)" }}>
                        <IcChevron size={17} />
                      </span>
                    </button>
                  ))}
                </div>
                <p className="faint" style={{ padding: "8px 6px 0" }}>
                  כדי למחוק קבוצה שלמה: הגדרות ← תגיות לאנשי קשר.
                </p>
              </section>
            )}

            {/* Reachable from the screen where the problem is visible. Finding a
                duplicate means seeing the same person twice in a list, and this
                is that list. */}
            <Link to="/duplicates" className="card list-row" style={{ marginBottom: 12 }}>
              <span
                className="rail-ico tint-red"
              >
                <IcMerge />
              </span>
              <span className="grow">
                כפילויות
                <span className="faint" style={{ display: "block", marginTop: 2 }}>
                  כרטיסים עם אותו טלפון או מייל. אפשר לאחד, או לסמן שאלה אנשים שונים.
                </span>
              </span>
              <IcChevron size={16} />
            </Link>

            <button className="btn btn-wide" onClick={() => setShowAll(true)}>
              הצגת כל {data.length} אנשי הקשר
            </button>
          </>
        )}
      </div>

      {/* Selection lives in a bar pinned above the nav, not inline in the list:
          the count and the destructive action must stay visible while scrolling
          through a few hundred rows. */}
      {isAdmin && visible.length > 0 && (
        <SelectionBar
          selecting={selecting}
          selected={selected}
          visible={visible}
          onStart={() => setSelecting(true)}
          onCancel={leaveSelection}
          onSelectAll={() => setSelected(new Set(visible.map((p) => p.id)))}
          onClear={() => setSelected(new Set())}
          onDeleted={leaveSelection}
        />
      )}
    </div>
  );

  /**
   * One search box, three kinds of answer.
   *
   * People, the events themselves, and the subjects on the timeline. They are
   * shown as separate sections rather than merged into one ranked list because
   * they are not comparable: a person is somebody to open, an event is a screen
   * with its registrations, and a subject is a group of people who share a
   * history. Ranking them against each other would put a note on one card above
   * the fifty-seven people who came to מכירת חמץ.
   */
  function results(list: Person[], q: string, error: string | null) {
    // "No results" is a claim about the data. A failed query is a claim about
    // the system, and reporting the first when the second happened is how a
    // denied read passes for an empty database.
    if (error) {
      return (
        <div className="notice error">
          החיפוש לא עבד. זו תקלה, לא „אין תוצאות”.
          <br />
          <span className="faint faint-xs mono">
            {error}
          </span>
        </div>
      );
    }

    const nothing =
      list.length === 0 &&
      events.data.length === 0 &&
      subjects.subjects.length === 0 &&
      !subjects.loading;

    if (nothing) {
      return (
        <div className="empty">
          <div className="empty-ico">
            <IcSearch size={26} />
          </div>
          <h3>אין תוצאות עבור „{q}”</h3>
          {/* The old text said the search starts at the beginning of the name.
              It no longer does, and saying so sent people looking for the wrong
              thing. */}
          <p>
            אפשר לחפש אנשי קשר לפי כל חלק מהשם או מהטלפון, ואירועים ונושאים לפי כל מילה
            בכותרת. למשל: „גמרא” או „חמץ”.
          </p>
        </div>
      );
    }

    return (
      <>
        {list.length > 0 && (
          <PersonList
            people={list}
            caption={count(list.length, NOUNS.result)}
            tagNameById={tagNameById}
            selecting={selecting}
            selected={selected}
            onToggle={toggle}
          />
        )}

        {events.data.length > 0 && (
          <section className="section">
            <div className="section-head">
              <div
                className="section-ico tint-green"
              >
                <IcTent size={17} />
              </div>
              <div className="section-title">
                אירועים <span className="section-n">{events.data.length}</span>
              </div>
            </div>
            <div className="card">
              {events.data.map((ev, i) => (
                <Link
                  to={"/events/" + ev.id}
                  key={ev.id}
                  className="list-row"
                  style={{ borderBottom: i < events.data.length - 1 ? undefined : "0" }}
                >
                  <span
                    className="rail-ico tint-green"
                  >
                    <IcCalendar size={16} />
                  </span>
                  <span className="grow">
                    <span className="name-row">
                      {ev.title ?? "אירוע"}
                    </span>
                    {/* The sign-up count is the reason to open an event from a
                        search — "איך אני מגיע לכל הנרשמים למשהו ספציפי" was asked
                        in those words, and the event screen is the answer. */}
                    <span className="muted">
                      {[
                        ev.startDate ?? null,
                        (ev.counts?.registered ?? 0) > 0 ? ev.counts?.registered + " נרשמים" : null,
                        (ev.counts?.attendedPeople ?? 0) > 0
                          ? ev.counts?.attendedPeople + " נכחו"
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <IcChevron size={16} />
                </Link>
              ))}
            </div>
          </section>
        )}

        {subjects.error ? (
          <div className="notice error" style={{ marginTop: 12 }}>
            החיפוש בציר הזמן לא עבד. זו תקלה, לא „אין תוצאות”.
            <br />
            <span className="faint faint-xs mono">
              {subjects.error}
            </span>
          </div>
        ) : subjects.loading ? (
          <p className="faint" style={{ padding: "12px 6px" }}>
            מחפשים בציר הזמן…
          </p>
        ) : (
          subjects.subjects.length > 0 && (
            <SubjectResults subjects={subjects.subjects} truncated={subjects.truncated} />
          )
        )}
      </>
    );
  }
}

/**
 * Deleting a lot of people at once, with the one warning that matters.
 *
 * Chunked because one call may delete at most 300 people; the loop keeps the
 * count honest even when a later chunk fails.
 *
 * ## Why the donation warning is not decoration
 *
 * This bar's main use is the "ללא תגית" list — 2,129 people nobody ever
 * classified, with a "select all 500" beside it, built so that an address-book
 * import could be cleared out. **Sixty-one of those people carry ₪51,353 in
 * donations.** The server used to refuse only on a *verified* donation and there
 * are none in this database, so the refusal could not fire; the fix is on the
 * server, and this is the half that tells a person what they are about to do
 * before they do it.
 *
 * A blocked delete is now reported as blocked, with the sum, and forcing it
 * takes a second deliberate tap. The donations themselves are never destroyed —
 * they are unlinked — but the link from money to the human being who gave it
 * does not come back, and that link is the entire value of a donor record.
 */
const SELECTION_FAMILY_ROLES: { id: string; label: string }[] = [
  { id: "father", label: "אבא" },
  { id: "mother", label: "אמא" },
  { id: "child", label: "ילד/ה" },
  { id: "spouse", label: "בן/בת זוג" },
];

function SelectionBar({
  selecting,
  selected,
  visible,
  onStart,
  onCancel,
  onSelectAll,
  onClear,
  onDeleted,
}: {
  selecting: boolean;
  selected: Set<string>;
  visible: Person[];
  onStart: () => void;
  onCancel: () => void;
  onSelectAll: () => void;
  onClear: () => void;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [combining, setCombining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  /** People the server refused because they have giving history, and how much. */
  const [held, setHeld] = useState<{ ids: string[]; totalMinor: number } | null>(null);

  const ids = [...selected];
  const selectedPeople = visible.filter((p) => selected.has(p.id));
  const CHUNK = 300;

  async function run(force = false) {
    setBusy(true);
    setMsg(null);
    const target = force ? (held?.ids ?? []) : ids;
    let deleted = 0;
    const blockedIds: string[] = [];
    let blockedMinor = 0;
    try {
      for (let i = 0; i < target.length; i += CHUNK) {
        const r = await deletePeople(target.slice(i, i + CHUNK), force);
        deleted += r.deleted;
        blockedIds.push(...r.blockedByVerifiedDonations);
        blockedMinor += r.blockedDonationTotalMinor ?? 0;
        setMsg(`נמחקו ${deleted} מתוך ${target.length}…`);
      }
      setConfirming(false);
      if (blockedIds.length > 0) {
        setHeld({ ids: blockedIds, totalMinor: blockedMinor });
        setMsg(`מחקנו ${deleted} אנשי קשר.`);
      } else {
        setHeld(null);
        setMsg(`מחקנו ${deleted} אנשי קשר.`);
        setTimeout(onDeleted, 1600);
      }
    } catch (e) {
      setMsg(
        `מחקנו ${deleted}, ואז הפעולה נעצרה: ${e instanceof Error ? e.message : "תקלה לא ידועה"}`,
      );
    } finally {
      setBusy(false);
    }
  }

  if (!selecting) {
    return (
      <div className="selbar">
        <button className="btn" onClick={onStart}>
          <IcCheck size={16} /> בחירה מרובה
        </button>
      </div>
    );
  }

  return (
    <>
      {combining && selectedPeople.length >= 2 && (
        <CombineSelectedSheet
          people={selectedPeople}
          onClose={() => setCombining(false)}
          onDone={(doneMsg) => {
            setCombining(false);
            setMsg(doneMsg);
            setTimeout(onDeleted, 1800);
          }}
        />
      )}
      <div className="selbar">
        {msg && <div className="selbar-msg">{msg}</div>}

        {held ? (
          <>
            <span className="grow">
              {held.ids.length} לא נמחקו כי יש להם תרומות
              <span className="faint" style={{ display: "block" }}>
                {(held.totalMinor / 100).toLocaleString("he-IL")} ₪ בסך הכל. התרומות עצמן לא
                יימחקו, אבל הן יישארו בלי שם.
              </span>
            </span>
            <button className="btn" onClick={() => void run(true)} disabled={busy}>
              <span className="danger">{busy ? "מוחקים…" : "למחוק בכל זאת"}</span>
            </button>
            <button
              className="btn"
              onClick={() => {
                setHeld(null);
                setMsg(null);
                onDeleted();
              }}
              disabled={busy}
            >
              השאירו אותם
            </button>
          </>
        ) : confirming ? (
          <>
            <span className="grow">
              למחוק {ids.length} אנשי קשר?
              <span className="faint" style={{ display: "block" }}>
                גיבוי של כל איש קשר נשמר ביומן הביקורת. מי שיש לו תרומות לא יימחק בלי אישור
                נוסף שלכם.
              </span>
            </span>
            <button className="btn" onClick={() => void run()} disabled={busy}>
              <span className="danger">{busy ? "מוחקים…" : "כן, למחוק"}</span>
            </button>
            <button className="btn" onClick={() => setConfirming(false)} disabled={busy}>
              לא
            </button>
          </>
        ) : (
          <>
            <span className="grow">נבחרו {ids.length}</span>
            <button className="btn" onClick={onSelectAll} disabled={busy}>
              בחרו את כל {visible.length}
            </button>
            {ids.length >= 2 && (
              <button className="btn btn-primary" onClick={() => setCombining(true)} disabled={busy}>
                <IcMerge size={15} /> איחוד / משפחה
              </button>
            )}
            {ids.length > 0 && (
              <>
                <button className="btn" onClick={onClear} disabled={busy}>
                  ניקוי
                </button>
                <button className="btn" onClick={() => setConfirming(true)} disabled={busy}>
                  <IcTrash size={15} /> <span className="danger">מחיקה</span>
                </button>
              </>
            )}
            <button className="btn" onClick={onCancel} disabled={busy}>
              יציאה
            </button>
          </>
        )}
      </div>
    </>
  );
}

function CombineSelectedSheet({
  people,
  onClose,
  onDone,
}: {
  people: Person[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [familyMode, setFamilyMode] = useState(true);
  const [keepId, setKeepId] = useState(() => people[0]?.id ?? "");
  const [roles, setRoles] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    people.forEach((p, idx) => {
      if (p.householdRole === "father" || p.householdRole === "mother" || p.householdRole === "spouse") {
        out[p.id] = p.householdRole;
      } else if (p.isChild || p.householdRole === "child") {
        out[p.id] = "child";
      } else if (idx === 0) {
        out[p.id] = "father";
      } else {
        out[p.id] = "child";
      }
    });
    return out;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allIds = people.map((p) => p.id);
  const dropIds = allIds.filter((id) => id !== keepId);

  const fatherCount = people.filter((p) => (roles[p.id] ?? "child") === "father").length;
  const motherCount = people.filter((p) => (roles[p.id] ?? "child") === "mother").length;
  const spouseCount = people.filter((p) => (roles[p.id] ?? "child") === "spouse").length;
  const hasRoleDuplicates = fatherCount > 1 || motherCount > 1 || spouseCount > 1;

  async function doMergeOne() {
    if (!keepId || dropIds.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await mergePeople(keepId, dropIds);
      onDone(`איחדנו את ${people.length} הכרטיסים אל „${r.keepName}”.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לאחד. נסו שוב.");
      setBusy(false);
    }
  }

  async function doSaveFamily() {
    setBusy(true);
    setErr(null);
    try {
      const r = await linkDuplicateAsFamily(allIds, roles);
      const mergedCnt = r.mergedIds?.length ?? 0;
      onDone(
        `שמרנו את משפחת ${r.householdName ?? ""}` +
          (mergedCnt > 0 ? ` ואיחדנו אוטומטית ${mergedCnt} כרטיסים כפולים של ההורים.` : "."),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור כמשפחה. נסו שוב.");
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="איחוד כרטיסים או יצירת משפחה">
        <div className="sheet-grip" />
        <div className="sheet-head">
          <h2>איחוד או שיוך למשפחה · {people.length} כרטיסים</h2>
        </div>

        <div className="sheet-body">
          <div className="chips" style={{ marginBottom: 12 }}>
            <button
              type="button"
              className={familyMode ? "tagchip tagchip-on" : "tagchip"}
              onClick={() => setFamilyMode(true)}
              disabled={busy}
            >
              בני משפחה (סיווג הורה / ילד/ה + איחוד כפילויות)
            </button>
            <button
              type="button"
              className={!familyMode ? "tagchip tagchip-on" : "tagchip"}
              onClick={() => setFamilyMode(false)}
              disabled={busy}
            >
              זה אותו אדם בדיוק (איחוד לכרטיס אחד)
            </button>
          </div>

          {!familyMode ? (
            <>
              <p className="ask-q">בחרו איזה כרטיס להשאיר (השאר יתמזגו אליו):</p>
              {people.map((p) => (
                <label
                  key={p.id}
                  className="dup-row"
                  style={{
                    background: keepId === p.id ? "var(--orange-soft)" : undefined,
                    borderColor: keepId === p.id ? "var(--orange)" : "var(--line)",
                  }}
                >
                  <input
                    type="radio"
                    name="batch-keep"
                    checked={keepId === p.id}
                    onChange={() => setKeepId(p.id)}
                    disabled={busy}
                  />
                  <Avatar name={p.displayName ?? "?"} size={36} />
                  <span className="grow">
                    <span className="name-row">{p.displayName ?? "ללא שם"}</span>
                    <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                      {primaryPhone(p) ?? "אין טלפון"}
                    </span>
                  </span>
                </label>
              ))}
            </>
          ) : (
            <>
              <p className="ask-q">
                סמנו את התפקיד של כל אחד במשפחה (אם יש כמה כרטיסים לאבא או לאמא, סמנו את כולם והמערכת תאחד אותם אוטומטית):
              </p>
              {people.map((p) => {
                const currentRole = roles[p.id] ?? "child";
                return (
                  <div key={p.id} className="dup-row" style={{ flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                      <Avatar name={p.displayName ?? "?"} size={36} />
                      <span className="grow">
                        <span className="name-row">{p.displayName ?? "ללא שם"}</span>
                        <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                          {primaryPhone(p) ?? "אין טלפון"}
                        </span>
                      </span>
                    </div>
                    <div className="chips" style={{ marginBottom: 0 }}>
                      {SELECTION_FAMILY_ROLES.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className={currentRole === r.id ? "tagchip tagchip-on" : "tagchip"}
                          onClick={() => setRoles((prev) => ({ ...prev, [p.id]: r.id }))}
                          disabled={busy}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {hasRoleDuplicates && (
                <div className="notice info" style={{ marginTop: 8 }}>
                  סימנת{" "}
                  {[
                    fatherCount > 1 ? `${fatherCount} כרטיסים בתור „אבא”` : null,
                    motherCount > 1 ? `${motherCount} כרטיסים בתור „אמא”` : null,
                    spouseCount > 1 ? `${spouseCount} כרטיסים בתור „בן/בת זוג”` : null,
                  ]
                    .filter(Boolean)
                    .join(" ו־")}
                  . בלחיצה על השמירה, המערכת <b>תאחד אוטומטית</b> את הכרטיסים הכפולים של אותו הורה לכרטיס אחד ותקשר את כולם למשפחה אחת.
                </div>
              )}
            </>
          )}

          {err && (
            <div className="notice error" style={{ marginTop: 10 }}>
              {err}
            </div>
          )}

          <div className="btn-row" style={{ marginTop: 14 }}>
            {!familyMode ? (
              <button className="btn btn-primary" onClick={doMergeOne} disabled={busy}>
                {busy ? "מאחדים…" : `אחד ל„${people.find((p) => p.id === keepId)?.displayName ?? "כרטיס נבחר"}”`}
              </button>
            ) : (
              <button className="btn btn-primary" onClick={doSaveFamily} disabled={busy}>
                {busy
                  ? "שומרים משפחה…"
                  : hasRoleDuplicates
                    ? "שמור כמשפחה ואחד כפילויות"
                    : "שמור כמשפחה"}
              </button>
            )}
            <button className="btn" onClick={onClose} disabled={busy}>
              ביטול
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function PersonList({
  people,
  caption,
  showDays,
  tagNameById,
  selecting,
  selected,
  onToggle,
}: {
  people: Person[];
  caption?: string;
  showDays?: boolean;
  tagNameById: Map<string, string>;
  selecting?: boolean;
  selected?: Set<string>;
  onToggle?: (id: string) => void;
}) {
  return (
    <>
      {caption && (
        <div className="card-title" style={{ padding: "0 6px 8px" }}>
          {caption}
        </div>
      )}
      <div className="card">
        {people.map((p, i) => {
          const days = daysSinceContact(p);
          const badge = recordBadge(p, tagNameById);
          const isSel = selected?.has(p.id) ?? false;
          const border = i < people.length - 1 ? "1px solid var(--line)" : undefined;

          const inner = (
            <>
              {selecting && (
                <span className={isSel ? "selbox on" : "selbox"} aria-hidden>
                  {isSel && <IcCheck size={14} />}
                </span>
              )}
              <Avatar name={p.displayName ?? "?"} size={42} />
              <div className="grow">
                <p className="name-row">
                  {p.displayName ?? "ללא שם"}
                </p>
                <p className="muted mono t-sm">
                  {primaryPhone(p) ?? "אין טלפון"}
                </p>
                {badge && (
                  <span className={`rec-badge rec-${badge.kind}`}>
                    {badge.kind === "duplicate" && <IcMerge size={12} />}
                    {badge.label}
                  </span>
                )}
              </div>
              {showDays && days != null && (
                <span className="faint" style={{ flexShrink: 0, marginTop: 0 }}>
                  {days === 0 ? "היום" : `לפני ${days} ימים`}
                </span>
              )}
              {!selecting && (
                <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>
                  <IcChevron size={17} />
                </span>
              )}
            </>
          );

          // In selection mode a row toggles instead of navigating: tapping a row
          // to open somebody while trying to tick fifty boxes is maddening.
          return selecting ? (
            <button
              key={p.id}
              className="card-body row"
              onClick={() => onToggle?.(p.id)}
              style={{
                borderBottom: border,
                alignItems: "center",
                width: "100%",
                textAlign: "start",
                background: isSel ? "var(--orange-soft)" : undefined,
              }}
            >
              {inner}
            </button>
          ) : (
            <Link
              to={`/people/${p.id}`}
              key={p.id}
              className="card-body row"
              style={{ borderBottom: border, alignItems: "center" }}
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </>
  );
}

/**
 * What kind of record this row is, when that is not obvious from the name.
 *
 * Only one badge, and only when it explains something. A row with nothing
 * unusual about it gets none — a list where every row is decorated tells the
 * reader nothing.
 */
function recordBadge(
  p: Person,
  tagNameById: Map<string, string>,
): { kind: "duplicate" | "family" | "business" | "child"; label: string } | null {
  const shared = p.sharesPhoneWithPersonIds ?? [];
  if (shared.length > 0) {
    return { kind: "duplicate", label: `אותו טלפון כמו עוד ${shared.length} כרטיסים` };
  }
  if (p.isCoupleRecord) return { kind: "family", label: "כרטיס של זוג" };
  // Only a household the backfill actually classified as a family may be called
  // one. The imported grouping is by street address, and a building of twenty-two
  // people from five families would otherwise read as somebody's relatives.
  if ((p.familyMemberCount ?? 0) > 1) {
    const n = p.familyMemberCount;
    return {
      kind: "family",
      label:
        p.householdKind === "address_group"
          ? `משפחת ${p.familySurname ?? ""} · ${n} אנשי קשר`.trim()
          : `משפחת ${p.householdName ?? p.familySurname ?? ""} · ${n} בני בית`.trim(),
    };
  }
  if (p.organization) return { kind: "business", label: p.organization };
  if (p.isChild) {
    const school = p.education?.school;
    return { kind: "child", label: school ? `ילד · ${school}` : "ילד" };
  }
  // A business-service tag is the only tag worth surfacing in a list: it says
  // what somebody does, which is why you would be looking for them.
  const service = (p.businessServiceTagIds ?? [])[0];
  if (service && tagNameById.has(service)) {
    return { kind: "business", label: tagNameById.get(service)! };
  }
  return null;
}

/**
 * What the timeline knows about a subject, and who it happened to.
 *
 * Eighty attendance entries for one class are eighty rows of the same four
 * words, so the results collapse to one line per subject and the people sit
 * behind a tap. The count is the useful part of the closed row: "מכירת חמץ ·
 * 57 אנשים" answers the question most of the time without opening anything.
 *
 * One subject is expanded at a time, because expanding costs reads — the person
 * documents are fetched only for the subject actually being looked at.
 */
const KIND_LABEL: Record<string, string> = {
  attendance: "נוכחות",
  donation: "תרומה",
  call: "שיחה",
  whatsapp: "וואטסאפ",
  meeting: "פגישה",
  visit: "ביקור",
  message: "הודעה",
  note: "הערה",
  enquiry: "פנייה",
  service_request: "בקשת שירות",
  other: "פעילות",
};

function SubjectResults({ subjects, truncated }: { subjects: SubjectHit[]; truncated: boolean }) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="section">
      <div className="section-head">
        <div
          className="section-ico tint-slate"
        >
          <IcSpark size={17} />
        </div>
        <div className="section-title">
          בציר הזמן <span className="section-n">{subjects.length}</span>
        </div>
      </div>

      {truncated && (
        <p className="faint" style={{ padding: "0 6px 8px" }}>
          יש יותר מ־600 תוצאות, ומוצגות האחרונות. הוסיפו מילה לחיפוש כדי לצמצם.
        </p>
      )}

      <div className="card">
        {subjects.map((s, i) => {
          const isOpen = open === s.title;
          return (
            <div
              key={s.title}
              style={{ borderBottom: i < subjects.length - 1 ? "1px solid var(--line)" : undefined }}
            >
              <button
                className="list-row"
                style={{ borderBottom: "0", width: "100%", textAlign: "start" }}
                onClick={() => setOpen(isOpen ? null : s.title)}
                aria-expanded={isOpen}
              >
                <span className="grow">
                  <span className="name-row">
                    {s.title}
                  </span>
                  <span className="muted">
                    {[
                      KIND_LABEL[s.kind] ?? KIND_LABEL.other,
                      count(s.personIds.length, NOUNS.people),
                      s.lastAt
                        ? new Date(s.lastAt).toLocaleDateString("he-IL", {
                            month: "long",
                            year: "numeric",
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span
                  style={{
                    color: "var(--ink-3)",
                    flexShrink: 0,
                    transform: isOpen ? "rotate(-90deg)" : undefined,
                  }}
                >
                  <IcChevron size={17} />
                </span>
              </button>
              {isOpen && <SubjectPeople personIds={s.personIds} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Everybody under one subject, with the one tap that reaches them.
 *
 * The row carries a WhatsApp link rather than only a name: finding the list and
 * then having to open each card to get a number is finding the list twice.
 */
function SubjectPeople({ personIds }: { personIds: string[] }) {
  const { data, loading, error, omitted } = useNamedPeople(personIds);

  if (loading) {
    return (
      <p className="faint" style={{ padding: "0 14px 12px", margin: 0 }}>
        טוען {personIds.length}…
      </p>
    );
  }
  if (error) {
    return (
      <p className="faint danger" style={{ padding: "0 14px 12px", margin: 0 }}>
        לא הצלחנו לטעון את הרשימה: {error}
      </p>
    );
  }

  return (
    <div style={{ padding: "0 0 6px" }}>
      {omitted > 0 && (
        <p className="faint" style={{ padding: "0 14px 6px", margin: 0 }}>
          מוצגים {data.length}. עוד {omitted} לא נטענו.
        </p>
      )}
      {data.map((p) => {
        const phone = primaryPhone(p);
        const wa = whatsappLink(phone);
        return (
          <div
            key={p.id}
            className="row"
            style={{ padding: "8px 14px", alignItems: "center", gap: 10 }}
          >
            <Link
              to={"/people/" + p.id}
              className="grow row"
              style={{ alignItems: "center", gap: 10 }}
            >
              <Avatar name={p.displayName ?? "?"} size={30} />
              <span className="grow">
                <span className="t-md" style={{ display: "block" }}>
                  {p.displayName ?? "ללא שם"}
                </span>
                <span className="faint faint-xs mono">
                  {phone ?? "אין טלפון"}
                </span>
              </span>
            </Link>
            {wa ? (
              <a className="icon-btn" href={wa} target="_blank" rel="noreferrer" aria-label="וואטסאפ">
                <IcWhatsapp size={16} />
              </a>
            ) : (
              <span className="icon-btn" aria-hidden style={{ opacity: 0.25 }}>
                <IcPhone size={16} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
